import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'products-pricing', title: 'Products & Pricing' },
  { id: 'orders-payments', title: 'Orders & Payments' },
  { id: 'shipping', title: 'Shipping & Delivery' },
  { id: 'returns', title: 'Returns & Refunds' },
  { id: 'ip', title: 'Intellectual Property' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'contact', title: 'Contact' },
];

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('acceptance');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const sectionEls = sections.map(s => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = 'Terms of Service - AiShopio Dubai UAE Shopping';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'AiShopio Dubai terms of service. Read our policies on orders, payments, UAE shipping, 7-day returns and refunds. Governed by Dubai UAE law.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service - AiShopio Dubai',
      description: 'AiShopio Dubai terms of service covering orders, payments, shipping and returns.',
      url: `${siteUrl}/terms`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${siteUrl}/terms` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="terms"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'terms');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="terms"]')?.remove(); };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-8 px-6" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-2">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-600">Terms of Service</span>
          </p>
          <p className="text-gray-500 text-sm">Last updated: January 1, 2024 &bull; Effective: January 1, 2024</p>
          <div className="mt-4 p-4 rounded-2xl bg-orange-50 border border-orange-100">
            <p className="text-sm text-gray-700">
              <i className="ri-information-line text-orange-500 mr-2"></i>
              By using AiShopio, you agree to these Terms of Service. These terms are governed by the laws of Dubai, UAE.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Mobile TOC pills */}
        <div className="lg:hidden flex flex-wrap gap-2 mb-8">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                activeSection === s.id
                  ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-600'
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>

        <div className="flex gap-8">
          {/* Sticky TOC - desktop only */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24 bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-5">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Contents</h3>
              <nav className="space-y-1">
                {sections.map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-200 cursor-pointer ${
                      activeSection === s.id
                        ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold'
                        : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div ref={contentRef} className="flex-1 max-w-4xl space-y-10">
            <section id="acceptance" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">1</span>
                Acceptance of Terms
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>By accessing or using the AiShopio website (aishopio.ae) and placing orders, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
                <p>These terms apply to all visitors, users, and customers of AiShopio. We reserve the right to update these terms at any time, with changes effective upon posting to the website.</p>
                <p>You must be at least 18 years old to make purchases on AiShopio. By placing an order, you confirm that you meet this age requirement.</p>
              </div>
            </section>

            <section id="products-pricing" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">2</span>
                Products &amp; Pricing
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>All prices on AiShopio are displayed in UAE Dirhams (AED) and include 5% Value Added Tax (VAT) as required by the UAE Federal Tax Authority.</p>
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                  <p className="font-semibold text-gray-800 mb-2">Pricing Policy:</p>
                  <ul className="space-y-1">
                    {[
                      'All prices are in AED (UAE Dirhams) inclusive of 5% VAT',
                      'Prices may change without prior notice',
                      'Promotional prices are valid for the specified period only',
                      'We reserve the right to cancel orders placed at incorrect prices',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p>Product images are for illustrative purposes. Actual products may vary slightly in color or appearance due to photography and screen settings.</p>
              </div>
            </section>

            <section id="orders-payments" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">3</span>
                Orders &amp; Payments
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>By placing an order, you make an offer to purchase the selected products. AiShopio reserves the right to accept or decline any order.</p>
                <p><strong className="text-gray-800">Accepted Payment Methods:</strong></p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: 'ri-money-dollar-circle-line', label: 'Cash on Delivery', color: 'bg-green-50 text-green-700' },
                    { icon: 'ri-bank-card-line', label: 'Credit/Debit Card', color: 'bg-gray-50 text-gray-700' },
                    { icon: 'ri-apple-line', label: 'Apple Pay', color: 'bg-gray-50 text-gray-700' },
                  ].map((p, i) => (
                    <div key={i} className={`flex flex-col items-center gap-2 p-3 rounded-xl ${p.color}`}>
                      <i className={`${p.icon} text-2xl`}></i>
                      <span className="text-xs font-semibold text-center">{p.label}</span>
                    </div>
                  ))}
                </div>
                <p>Orders can be cancelled within 2 hours of placement. Once dispatched, orders cannot be cancelled but may be returned per our Returns Policy.</p>
              </div>
            </section>

            <section id="shipping" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">4</span>
                Shipping &amp; Delivery (UAE Emirates)
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>AiShopio delivers to all 7 UAE Emirates:</p>
                <div className="flex flex-wrap gap-2">
                  {['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'].map(e => (
                    <span key={e} className="px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100">
                      <i className="ri-map-pin-line mr-1"></i>{e}
                    </span>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between p-3 rounded-xl bg-gray-50">
                    <span className="font-semibold text-gray-800">Standard Delivery (3-5 days)</span>
                    <span className="text-green-600 font-bold">FREE on orders over AED 200</span>
                  </div>
                  <div className="flex justify-between p-3 rounded-xl bg-gray-50">
                    <span className="font-semibold text-gray-800">Standard Delivery (3-5 days)</span>
                    <span className="font-bold text-gray-800">AED 25 (under AED 200)</span>
                  </div>
                  <div className="flex justify-between p-3 rounded-xl bg-gray-50">
                    <span className="font-semibold text-gray-800">Express Delivery (Next Day)</span>
                    <span className="font-bold text-gray-800">AED 35</span>
                  </div>
                </div>
                <p>Delivery times are estimates and may vary due to circumstances beyond our control. AiShopio is not liable for delays caused by third-party logistics providers.</p>
              </div>
            </section>

            <section id="returns" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">5</span>
                Returns &amp; Refunds (7 Days)
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                  <p className="font-bold text-green-800 text-base mb-1">7-Day Return Policy</p>
                  <p className="text-green-700">You have 7 days from the date of delivery to return eligible items for a full refund.</p>
                </div>
                <p><strong className="text-gray-800">Return Conditions:</strong> Items must be unused, in original packaging, with all tags attached. Proof of purchase required.</p>
                <p><strong className="text-gray-800">Non-Returnable Items:</strong> Opened skincare/cosmetics, personal hygiene products, and items marked as final sale.</p>
                <p><strong className="text-gray-800">Refund Process:</strong> Refunds are processed within 3-5 business days after we receive and inspect the returned item. Refunds are issued to the original payment method.</p>
                <p>To initiate a return, contact us via WhatsApp at +971 50 000 0000 or email returns@aishopio.ae.</p>
              </div>
            </section>

            <section id="ip" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">6</span>
                Intellectual Property
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>All content on the AiShopio website, including but not limited to text, graphics, logos, images, and software, is the property of AiShopio and is protected by UAE intellectual property laws.</p>
                <p>You may not reproduce, distribute, modify, or create derivative works from any content on this website without our express written permission.</p>
                <p>The AiShopio name, logo, and brand are registered trademarks. Unauthorized use is strictly prohibited.</p>
              </div>
            </section>

            <section id="liability" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">7</span>
                Limitation of Liability
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <p>To the maximum extent permitted by UAE law, AiShopio shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.</p>
                <p>Our total liability to you for any claim arising from these terms or your use of our services shall not exceed the amount you paid for the specific order giving rise to the claim.</p>
                <p>We do not warrant that our website will be uninterrupted, error-free, or free from viruses or other harmful components.</p>
              </div>
            </section>

            <section id="governing-law" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">8</span>
                Governing Law
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed space-y-3">
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                  <p className="font-bold text-gray-800 mb-2">Jurisdiction</p>
                  <p>These Terms of Service are governed by and construed in accordance with the laws of the Emirate of Dubai and the United Arab Emirates.</p>
                </div>
                <p>Any disputes arising from these terms or your use of AiShopio shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.</p>
                <p>In the event of a dispute, we encourage you to first contact us directly to seek an amicable resolution before pursuing legal action.</p>
              </div>
            </section>

            <section id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-600 text-white text-sm font-bold">9</span>
                Contact
              </h2>
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 text-gray-700 text-sm leading-relaxed">
                <p className="mb-4">For questions about these Terms of Service, contact us:</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3"><i className="ri-mail-line text-orange-500"></i><span>legal@aishopio.ae</span></div>
                  <div className="flex items-center gap-3"><i className="ri-whatsapp-line text-green-500"></i><span>+971 50 000 0000</span></div>
                  <div className="flex items-center gap-3"><i className="ri-map-pin-line text-orange-500"></i><span>Dubai, United Arab Emirates</span></div>
                </div>
                <p className="mt-4 text-gray-500">These Terms of Service were last updated on January 1, 2024.</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
